import { Box } from '@mui/material';
import { ProjectPhase } from './ProjectPhase';
import useFetch from '../../../../api/useFetch';
import { useTranslation } from 'react-i18next';
import { PhaseForm } from './PhaseForm';
import { useState } from 'react';
import { updateOrCreate } from '../../../special/updateOrCreate';
import { ProjectAddElementButton } from '../ProjectAddElementButton';
import { ProjectSkeleton } from '../ProjectSkeleton';
import { ApplyProjectPreset } from '../../../../scenes/projects/ApplyProjectPreset';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { DBRequest, API_PUT, API_POST, useConfig } from '../../../../api/api';
import ExoDialog from '../../ExoDialog';
import { PresetAddElementButton } from '../PesetAddElementButton';

export const ProjectPhaseStack = ({ project, editmode }) => {
  const { t } = useTranslation();
  const config = useConfig();

  const { apiData, setApiData } = useFetch(`projects/${project.id}/phases`);

  const [open, setOpen] = useState(false);

  // state for open apply preset dialog
  const [openPreset, setOpenPreset] = useState(false);
  const [edit, setEdit] = useState(null);
  const [isSorting, setIsSorting] = useState(false);
  const [activePhase, setActivePhase] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleChange(data) {
    setApiData(updateOrCreate(apiData, data, true));
  }

  function handleDelete(phaseToDelete) {
    const newPhases = apiData.data.filter((obj) => obj.id !== phaseToDelete.id);
    setApiData({ ...apiData, data: newPhases });
  }

  // function to handle applying preset, calls api and then refetches phases
  function handleApplyPreset(data) {
    DBRequest({
      config,
      path: `projects/${project.id}/apply-preset`,
      method: API_POST(data),
      onResponse: (res) =>
        setApiData((prev) => ({ ...prev, data: res.data.phases })),
    });
  }

  function handleDragStart(event) {
    setIsSorting(true);
    const phase = apiData.data.find((p) => p.id === event.active.id);
    setActivePhase(phase);
  }

  function handleDragEnd(event) {
    setIsSorting(false);
    setActivePhase(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = apiData.data.findIndex((p) => p.id === active.id);
    const newIndex = apiData.data.findIndex((p) => p.id === over.id);

    const newData = arrayMove(apiData.data, oldIndex, newIndex);
    setApiData({ ...apiData, data: newData });

    DBRequest({
      config,
      path: `project-phases/${active.id}`,
      method: API_PUT({ sortIndex: newIndex }),
    });
  }

  if (!apiData) return <ProjectSkeleton />;
  return (
    <Box className="flex flex-col gap-10 pt-4">
      {editmode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={apiData.data.map((p) => p.id)}
            strategy={verticalListSortingStrategy}
          >
            {apiData.data.map((phase, index) => (
              <ProjectPhase
                project={project}
                key={phase.id}
                editmode={editmode}
                phase={phase}
                onEdit={setEdit}
                onPhaseChange={handleChange}
                onDelete={handleDelete}
                nr={index + 1}
                isSorting={isSorting}
              />
            ))}
          </SortableContext>
          <DragOverlay>
            {activePhase ? (
              <ProjectPhase
                project={project}
                editmode={editmode}
                phase={activePhase}
                onEdit={() => {}}
                onPhaseChange={() => {}}
                onDelete={() => {}}
                nr={apiData.data.findIndex((p) => p.id === activePhase.id) + 1}
                isSorting={true}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        apiData.data.map((phase, index) => (
          <ProjectPhase
            project={project}
            key={phase.id}
            editmode={editmode}
            phase={phase}
            onEdit={setEdit}
            onPhaseChange={handleChange}
            onDelete={handleDelete}
            nr={index + 1}
          />
        ))
      )}
      {editmode && (
        <Box className="flex flex-row gap-2">
          <ProjectAddElementButton
            label={t('Add Phase')}
            onClick={() => setOpen(true)}
            className="backdrop-blur-lg basis-[70%] grow"
          />
          {/* button to ADD element from preset */}
          <PresetAddElementButton
            label={t('Add element from preset')}
            onClick={() => setOpenPreset(true)}
            className="backdrop-blur-lg basis-[30%] grow"
            sizePx={40}
          />
        </Box>
      )}
      <ExoDialog
        open={openPreset}
        limitWidth
        onClose={() => setOpenPreset(false)}
      >
        <ApplyProjectPreset
          presetType="project"
          relations={['phases']}
          onApply={handleApplyPreset}
          onClose={() => setOpenPreset(false)}
        />
      </ExoDialog>

      <PhaseForm
        projectId={project.id}
        open={open || edit}
        onClose={() => {
          setEdit(null);
          setOpen(false);
        }}
        onChange={handleChange}
        startDataCollection={edit}
      />
    </Box>
  );
};
